import React, { useEffect, useState } from 'react'
import { PartnerService } from '@duck/api-definitions'
import { cn } from '@duck/ui'
import { useTar } from '@/modules/i18n'

const S = PartnerService.AssignmentDetailsStatus

interface StatusPillProps {
  status?: PartnerService.AssignmentDetailsStatus
  /** Submitted but feedback not yet available → show a processing pill instead. */
  processing?: boolean
  /** Person has already joined (public-link joiner, or a regular assignee whose onboarding is done) → label Sent as "Joined". */
  joined?: boolean
}

// Isolated so its 600ms tick re-renders only the badge, not the whole roster.
const ProcessingPill: React.FC<{ label: string }> = ({ label }) => {
  const [dots, setDots] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setDots((d) => (d + 1) % 4), 600)
    return () => clearInterval(id)
  }, [])
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs italic text-muted-foreground">
      {label}
      {/* Fixed-width dots so the pill doesn't resize each tick. */}
      <span aria-hidden className="inline-block w-[1.2ch] text-start">{'.'.repeat(dots)}</span>
    </span>
  )
}

export const StatusPill: React.FC<StatusPillProps> = ({ status, processing, joined }) => {
  const { t } = useTar()
  if (processing) return <ProcessingPill label={t('common.processing')} />
  // Intentional status language (Sent=muted, Started=blue, Submitted=success).
  const map: Partial<Record<PartnerService.AssignmentDetailsStatus, { label: string; cls: string }>> = {
    [S.Sent]: joined
      ? { label: t('common.joined'), cls: 'border-primary/40 bg-primary/10 text-primary' }
      : { label: t('common.sent'), cls: 'border-border bg-muted/40 text-muted-foreground' },
    [S.Started]: { label: t('common.started'), cls: 'border-blue-500/40 bg-blue-500/10 text-blue-500 dark:text-blue-400' },
    [S.Submitted]: { label: t('common.submitted'), cls: 'border-success/40 bg-success/10 text-success' },
    [S.Forfeited]: { label: t('common.forfeited'), cls: 'border-border bg-muted/30 text-muted-foreground' },
  }
  const entry = status ? map[status] : undefined
  if (!entry) return null
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs', entry.cls)}>
      {entry.label}
    </span>
  )
}
